import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid';
import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { openUrl, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doReportingGetReportListData } from 'store/actions';
import {
  selectRptReportItem,
  selectRptReportLoaded,
  selectRptReportOrder,
  selectRptReportTotalCount,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { IconButton } from 'components/IconButton';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { smartText } from 'lib/str';
import { ReportingGridLoader } from '../../ReportingGridLoader';
import { Header } from './Header';
import { Visuals } from './Visuals';

type Report = ReportTypes[ReportEnum.assets];
type DefinedGridProps = GridProps<
  Report['columns'],
  string,
  { reportKey: string }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const data = useSelector((state) =>
    selectRptReportItem(state, extraData.reportKey, id),
  ) as undefined | Report['structure'];

  return React.useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      onPress: () => {
        // console.log('Press');
      },
      columns: {
        asset: () => {
          const { engageUrl } = data.asset;
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1} weight="bold">
                  {data.asset.name}
                </Txt>
                <Txt size="xsmall" numberOfLines={1}>
                  {rbAddReferencePrefix(data.asset.reference)}
                  {data.asset.customReference
                    ? ` • ${data.asset.customReference}`
                    : ''}
                </Txt>
              </View>
              <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf / 2}
                separatorColor="transparent"
                style={{ alignItems: 'center' }}
              >
                {!!engageUrl && (
                  <IconButton
                    icon={ICONS.ArrowUpRightFromSquareLight}
                    onPress={() => openUrl(engageUrl)}
                    iconSize={12}
                    accessibilityLabel="Open engage website for this asset"
                    accessibilityHint={smartText(
                      "Opens this asset's engage website",
                    )}
                  />
                )}
              </ViewWithSeparators>
            </View>
          );
        },
        address: () => {
          const addressLines: string[] = data.asset.postcode
            ? [...data.asset.addressLines] // [...data.asset.addressLines, data.asset.postcode]
            : data.asset.addressLines;
          return (
            <View
              style={{
                flex: 1,
              }}
            >
              {!!addressLines.length && (
                <Txt>
                  {!!data.asset.parentName && (
                    <Txt weight="medium">{data.asset.parentName}</Txt>
                  )}
                  {data.asset.parentName && addressLines.length ? ', ' : ''}
                  {addressLines.join(', ')}
                  {addressLines.length && data.asset.postcode ? ', ' : ''}
                  <Txt weight="medium" numberOfLines={1}>
                    {data.asset.postcode}
                  </Txt>
                </Txt>
              )}
            </View>
          );
        },
      },
    };
  }, [data]);
};

export const Main: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Asset',
      dataIndex: 'asset',
      width: 200,
      flex: 1,
    },
    {
      name: 'Address',
      dataIndex: 'address',
      minWidth: 200,
      flex: 2,
    },
  ]);

  const reportLoaded = useSelector((state) =>
    selectRptReportLoaded(state, reportKey),
  );
  const reportOrder = useSelector((state) =>
    selectRptReportOrder(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );
  const serverCount = reportTotalCount - reportOrder.length;

  const handleLoadReportData = React.useCallback(() => {
    dispatch(
      doReportingGetReportListData({
        key: reportKey,
        nextPage: true,
        closeModals: false,
        overlayLoader: false,
      }),
    );
  }, [dispatch, reportKey]);

  return (
    <Page header={<Header reportKey={reportKey} />}>
      <View style={{ flex: 1 }}>
        <ReportingGridLoader reportKey={reportKey} />
        {!reportLoaded ? (
          <PageEmpty
            text="Waiting..."
            subtext="Press load to get started"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : !reportOrder.length ? (
          <PageEmpty
            text="No Results Found"
            subtext="Try changing your filter"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            ListHeaderComponent={<Visuals reportKey={reportKey} />}
            columns={columns}
            data={reportOrder}
            extraData={{ reportKey }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              serverCount > 0 ? (
                <View
                  style={{
                    padding: theme.padding * 2,
                    paddingTop: theme.padding,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      serverCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadReportData}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.padding * 2 }} />
              )
            }
            // refreshControl,
          />
        )}
      </View>
    </Page>
  );
};
